import { z } from "zod";

import { stableStringify } from "~/shared/utils/stable-stringify";

import {
  ActionBlockExtraRowSchema,
  ActionBlockGroupSchema,
  ActionBlockRowSchema,
  ActionLinkSchema,
  ActionRowQuestionSchema,
} from "./action-block";

export const ACTION_BINDINGS_MAX_PER_TURN = 8;
/** Upper bound on any review's items, so selection and pick arrays stay bounded for every action. */
export const ACTION_REVIEW_ITEM_LIMIT = 300;

export const ActionProposalStatusSchema = z.enum([
  "PENDING",
  "REPLACED",
  "DISMISSED",
  "EXPIRED",
  "APPLYING",
  "APPLIED",
  "PARTIALLY_APPLIED",
  "STALE",
  "FAILED",
]);
export type ActionProposalStatus = z.infer<typeof ActionProposalStatusSchema>;

export const ActionProposalItemStatusSchema = z.enum(["NOT_SELECTED", "PENDING", "APPLIED", "STALE", "REJECTED", "FAILED"]);
export type ActionProposalItemStatus = z.infer<typeof ActionProposalItemStatusSchema>;

export const ActionProposalItemEligibilitySchema = z.enum(["READY", "NEEDS_INPUT", "UNSUPPORTED"]);

/** One reviewed item as the browser sees it: a server-built row, never the action's stored presentation. */
export const ActionProposalItemSchema = z
  .object({
    ordinal: z.number().int().nonnegative(),
    itemKey: z.string().min(1),
    operationKind: z.string().min(1),
    eligibility: ActionProposalItemEligibilitySchema,
    selected: z.boolean(),
    status: ActionProposalItemStatusSchema,
    outcomeCode: z.string().min(1).nullable().optional(),
    outcomeMessage: z.string().min(1).nullable().optional(),
    resultEntityId: z.string().uuid().nullable().optional(),
    row: ActionBlockRowSchema,
    question: ActionRowQuestionSchema.optional(),
    /** Saved-row links; a saved row whose links cannot be built carries `linkNote` instead. */
    links: z.array(ActionLinkSchema).optional(),
    linkNote: z.string().min(1).optional(),
    /** Whether a saved row offers a correction. */
    correctable: z.boolean(),
    /** Larger first within a review's row order; absent sorts as zero. */
    sortKey: z.number().optional(),
  })
  .strict();
export type ActionProposalItem = z.infer<typeof ActionProposalItemSchema>;

export const ActionReceiptSummarySchema = z
  .object({
    total: z.number().int().nonnegative(),
    applied: z.number().int().nonnegative(),
    stale: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  })
  .strict();
export type ActionReceiptSummary = z.infer<typeof ActionReceiptSummarySchema>;

export const ActionProposalCountsSchema = z
  .object({
    candidates: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    needsInput: z.number().int().nonnegative(),
    unsupported: z.number().int().nonnegative(),
    selected: z.number().int().nonnegative(),
    /** Rows presented as left out, only when there are any; they are among the unsupported rows and cannot be saved in this review. */
    leftOut: z.number().int().nonnegative().optional(),
  })
  .strict();

/** How the review saves and how its selection changes, resolved by the action from the stored review. */
export const ActionPolicySchema = z
  .object({
    save: z.enum(["together", "each"]),
    select: z.enum(["fixed", "toggle", "replan"]),
    approvable: z.boolean(),
    reason: z.string().min(1).nullable(),
  })
  .strict();
export type ActionPolicy = z.infer<typeof ActionPolicySchema>;

const LabelPairSchema = z.object({ one: z.string().min(1), many: z.string().min(1) }).strict();

/**
 * Every review sentence that names the action's record or its home, chosen by the action for this
 * stored review. Templates carry `{count}`, `{applied}`, `{total}` or `{chosen}` slots.
 */
export const ActionLabelsSchema = z
  .object({
    approve: LabelPairSchema,
    change: z.string().min(1),
    footer: z.string().min(1).nullable(),
    consequence: z.string().min(1),
    receiptNonEffects: z.string().min(1),
    receiptFallback: z.object({ one: z.string().min(1), many: z.string().min(1), partial: z.string().min(1) }).strict(),
    correct: z.string().min(1),
    stale: LabelPairSchema,
    permissionLost: z.string().min(1),
    loadFailed: z.string().min(1),
    choose: LabelPairSchema,
    chosenProgress: z.string().min(1),
  })
  .strict();
export type ActionLabels = z.infer<typeof ActionLabelsSchema>;

export const ActionProposalArtifactSchema = z
  .object({
    artifactKind: z.literal("action-proposal"),
    orgId: z.string().uuid(),
    familyId: z.string().uuid(),
    proposalId: z.string().uuid(),
    version: z.number().int().min(1),
    supersedesProposalId: z.string().uuid().nullable(),
    capabilityKey: z.string().min(1),
    semanticVersion: z.string().min(1),
    executionMode: z.enum(["INDEPENDENT_ITEMS", "ATOMIC_BATCH"]),
    status: ActionProposalStatusSchema,
    expiresAt: z.string().datetime(),
    preparedAt: z.string().datetime(),
    lifecycleSequence: z.number().int().nonnegative(),
    resumable: z.boolean().default(false),
    refinement: z
      .union([
        z.object({ available: z.literal(true) }).strict(),
        z.object({ available: z.literal(false), reason: z.literal("conversation_retired") }).strict(),
      ])
      .optional(),
    title: z.string().min(1),
    subject: z.string().min(1),
    compactSummary: z.array(z.string().min(1).max(240)).max(4).optional(),
    counts: ActionProposalCountsSchema,
    policy: ActionPolicySchema,
    /** Up to three header context parts, shown joined under the title. */
    context: z.array(z.string().min(1)).max(3),
    headerLinks: z.array(ActionLinkSchema).max(3),
    labels: ActionLabelsSchema,
    display: z
      .object({
        /** Approve shows only in the full review, never on the card. */
        approveInFullReviewOnly: z.boolean(),
        /** A one-row review still shows its checkbox. */
        singleRowCheckbox: z.boolean(),
      })
      .strict(),
    groups: z.array(ActionBlockGroupSchema).max(12).optional(),
    extraRows: z
      .array(ActionBlockExtraRowSchema)
      .max(ACTION_REVIEW_ITEM_LIMIT + 12)
      .optional(),
    inlineItemLimit: z.number().int().positive(),
    itemPage: z
      .object({
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
      })
      .strict(),
    inlineItems: z.array(ActionProposalItemSchema).optional(),
    binding: z
      .object({ token: z.string().regex(/^[a-f0-9]{64}$/) })
      .strict()
      .optional(),
    approval: z
      .object({
        actorDisplayName: z.string().min(1),
        approvedAt: z.string().datetime(),
      })
      .strict()
      .optional(),
    receipt: ActionReceiptSummarySchema.optional(),
    receiptTitle: z.string().min(1).optional(),
    narration: z.string().min(1).optional(),
  })
  .strict();
export type ActionProposalArtifact = z.infer<typeof ActionProposalArtifactSchema>;

export const COMPLETE_ACTION_REVIEW_BYTE_LIMIT = 3_000_000;

export interface CompleteActionReviewDocument {
  /** Normal artifact metadata without its ephemeral binding or inline duplicate. */
  artifact: Omit<ActionProposalArtifact, "binding" | "inlineItems">;
  /** Every item in the exact representation returned by the paged review endpoint. */
  items: ActionProposalItem[];
}

export function completeActionReviewDocument(
  artifact: Omit<ActionProposalArtifact, "binding" | "inlineItems">,
  items: readonly ActionProposalItem[],
): CompleteActionReviewDocument {
  return { artifact, items: [...items] };
}

export function completeActionReviewByteLength(document: CompleteActionReviewDocument): number {
  return new TextEncoder().encode(stableStringify(document)).byteLength;
}

export function completeActionReviewFits(document: CompleteActionReviewDocument): boolean {
  return completeActionReviewByteLength(document) <= COMPLETE_ACTION_REVIEW_BYTE_LIMIT;
}

/** Fills a label template's `{name}` slots. */
export function fillActionLabel(template: string, values: Readonly<Record<string, number | string>>): string {
  return template.replace(/\{(\w+)\}/g, (slot, name: string) => (name in values ? String(values[name]) : slot));
}

export const ActionBindingSchema = z
  .object({
    proposalId: z.string().uuid(),
    version: z.number().int().min(1),
    token: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type ActionBinding = z.infer<typeof ActionBindingSchema>;

export const ActionDisplayedSelectionSchema = ActionBindingSchema.extend({
  selectedItemKeys: z.array(z.string().min(1).max(200)).max(ACTION_REVIEW_ITEM_LIMIT),
}).strict();
export type ActionDisplayedSelection = z.infer<typeof ActionDisplayedSelectionSchema>;

/** An answer to a row question: the row and the offered choice. The action maps the choice. */
export const ActionClarifyPickIntentSchema = z
  .object({
    itemKey: z.string().min(1).max(200),
    choiceId: z.string().min(1).max(200),
  })
  .strict();
export type ActionClarifyPickIntent = z.infer<typeof ActionClarifyPickIntentSchema>;

export const ActionIntentSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("approve"),
      selectedItemKeys: z.array(z.string().min(1).max(200)).max(ACTION_REVIEW_ITEM_LIMIT).optional(),
      proposalId: z.string().uuid(),
      version: z.number().int().min(1),
      token: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal("dismiss"),
      proposalId: z.string().uuid(),
      version: z.number().int().min(1),
      token: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal("resume"),
      proposalId: z.string().uuid(),
      version: z.number().int().min(1),
      token: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal("revise_selection"),
      proposalId: z.string().uuid(),
      version: z.number().int().min(1),
      token: z.string().regex(/^[a-f0-9]{64}$/),
      includeItemKeys: z.array(z.string().min(1)).max(ACTION_REVIEW_ITEM_LIMIT),
      excludeItemKeys: z.array(z.string().min(1)).max(ACTION_REVIEW_ITEM_LIMIT),
    })
    .strict(),
  z
    .object({
      kind: z.literal("clarify_picks"),
      proposalId: z.string().uuid(),
      version: z.number().int().min(1),
      token: z.string().regex(/^[a-f0-9]{64}$/),
      picks: z.array(ActionClarifyPickIntentSchema).min(1).max(ACTION_REVIEW_ITEM_LIMIT),
    })
    .strict(),
]);
export type ActionIntent = z.infer<typeof ActionIntentSchema>;

export const actionClarifyPicksIntent = (
  binding: ActionBinding,
  picks: ReadonlyArray<{ itemKey: string; choiceId: string }>,
): Extract<ActionIntent, { kind: "clarify_picks" }> => ({
  kind: "clarify_picks",
  proposalId: binding.proposalId,
  version: binding.version,
  token: binding.token,
  picks: picks.map(pick => ({ itemKey: pick.itemKey, choiceId: pick.choiceId })),
});

/** A reference to an exact review or saved result. It provides context, never approval authority. */
export const ActionDraftContextSchema = z
  .object({
    mode: z.enum(["change", "correct"]),
    conversationId: z.string().min(1).max(200),
    proposalId: z.string().uuid(),
    itemKey: z.string().min(1).optional(),
    resultEntityId: z.string().uuid().optional(),
    subject: z.string().min(1).max(240),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.mode === "correct" && (!value.itemKey || !value.resultEntityId)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "A correction must identify the exact saved result." });
    }
  });
export type ActionDraftContext = z.infer<typeof ActionDraftContextSchema>;
